import * as VoiceService from '@/admin/services/VoiceService'
import {
  CtiEventSource,
  CtiInteractionTypeEnum,
  LandingPageEnum,
  type CtiService,
  type CtiServiceWithInit,
  CtiStatus,
  CtiStatusColor,
  ctiStatusColorMap,
  type AgentModel,
  type CtiCallInfo,
  type IFrameStyle,
  type InboundCallConfigurationDTOV2,
  type OutboundCallConfigurationDTOV2,
  type ProviderGeneralConfigurationDTOV2,
  type CtiOutboundCallSource,
  VoiceProvider,
} from '@/common/types/cti'
import { defineStore } from 'pinia'
import { computed, type Ref, ref } from 'vue'
import { AuthoritiesEnum } from '@/common/types/session'
import { useSessionStore } from '@/common/stores/sessionStore'
import { useLocalStorage } from '@vueuse/core'
import dayjs from 'dayjs'

export const dummyCtiService: CtiService = {
  outboundCallFromEverDesk() {
    /* Dummy == NoOp */
  },
  release() {
    /* Dummy == NoOp */
  },
  reload() {
    /* Dummy == NoOp */
  },
  setAgentStatus() {
    /* Dummy == NoOp */
  },
}

const blankURL = new URL('about:blank')

export const useCtiStore = defineStore('cti', () => {
  const sessionStore = useSessionStore()

  const activeProvider: Ref<VoiceProvider> = ref(VoiceProvider.NONE)
  const token: Ref<string> = ref('')
  const toReinitialized: Ref<boolean> = ref(false)
  const connectable: Ref<boolean> = ref(false)
  const voiceIsInitialized: Ref<boolean> = ref(false)
  const dialogVisibility: Ref<boolean> = ref(false)
  const dialogStateSource: Ref<CtiEventSource> = ref(CtiEventSource.Automatic)
  const callerPhone: Ref<string | null> = ref('')
  const provider: Ref<CtiService | CtiServiceWithInit> = ref(dummyCtiService)
  const idGroup: Ref<string | null> = ref(null)
  const inboundCallConfiguration: Ref<InboundCallConfigurationDTOV2 | null> = ref(null)
  const outboundCallConfiguration: Ref<OutboundCallConfigurationDTOV2 | null> = ref(null)
  const url: Ref<URL> = ref(blankURL)
  const inboundCallsMap: Ref<Map<string, InboundCallConfigurationDTOV2>> = ref(new Map())
  const outboundCallsMap: Ref<Map<string, OutboundCallConfigurationDTOV2>> = ref(new Map())
  const outboundCallSource: Ref<CtiOutboundCallSource | null> = ref(null)
  const urlString: Ref<string> = ref('')
  const calledPhone: Ref<string | null> = ref(null)
  const customerId: Ref<number | null> = ref(null)
  const direction: Ref<CtiInteractionTypeEnum | null> = ref(null)
  const updateAtEnd: Ref<boolean> = ref(false)
  const landingPage: Ref<LandingPageEnum> = ref(LandingPageEnum.TICKET_HANDLING_SCREEN)
  const status: Ref<CtiStatus> = ref(CtiStatus.DISCONNECTED)
  const agentLogin: Ref<string | null> = ref(null)
  const ticketId: Ref<number | null> = ref(null)
  const userToUserInformation: Ref<string | undefined> = ref()
  const iframeStyle: Ref<IFrameStyle> = ref({
    narrowWidth: '320px',
    wideWidth: '560px',
    height: '200vh',
    innerPadding: '48px',
  })
  const generalConfiguration: Ref<ProviderGeneralConfigurationDTOV2 | undefined> = ref(undefined)
  const iframeUrlUpdate: Ref<boolean> = ref(false)

  const incorrectUser: Ref<boolean> = ref(false)

  const ctiPanelVisibility: Ref<boolean> = ref(false)
  const callTimer: Ref<number> = ref(0)
  const counter: Ref<number | null> = ref(null)
  const callTimerHuman: Ref<string> = computed(() => dayjs.duration(callTimer.value, 's').format('mm:ss'))

  const idCall = useLocalStorage<string | null>('idCall', null)
  const ctiCallInfo = useLocalStorage<CtiCallInfo | null>('ctiCallInfo', null)

  function statusColor(): CtiStatusColor {
    return ctiStatusColorMap.get(status.value) ?? CtiStatusColor.black
  }

  function toggleDialog(source: CtiEventSource) {
    dialogVisibility.value = !dialogVisibility.value
    dialogStateSource.value = source
  }

  function showDialog(source: CtiEventSource) {
    dialogVisibility.value = true
    dialogStateSource.value = source
  }

  function hideDialog(source: CtiEventSource) {
    dialogVisibility.value = false
    dialogStateSource.value = source
  }
  function startCallTimer() {
    stopCallTimer()
    counter.value = setInterval(() => callTimer.value++, 1000) as unknown as number
  }

  function stopCallTimer() {
    if (counter.value) clearInterval(counter.value as unknown as number)
    callTimer.value = 0
    counter.value = null
  }

  function startCallInCtiPanel() {
    startCallTimer()
    ctiPanelVisibility.value = true
  }

  function wrapUpCallInCtiPanel() {
    stopCallTimer()
    ctiPanelVisibility.value = false
  }

  function newInboundCall(
    newCallerPhone: string,
    newIdGroup: string | null,
    newCalledPhone: string | null,
    newIdCall: string | null,
    newUpdateAtEnd: boolean,
    newUuiData?: string,
  ) {
    direction.value = CtiInteractionTypeEnum.INBOUND
    showDialog(CtiEventSource.Automatic)
    callerPhone.value = newCallerPhone
    idGroup.value = newIdGroup
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    updateAtEnd.value = newUpdateAtEnd
    userToUserInformation.value = newUuiData
  }

  function newInboundCallV2(
    newCallerPhone: string,
    newInboundCallConfiguration: InboundCallConfigurationDTOV2 | null,
    newCalledPhone: string | null,
    newIdCall: string | null,
    newUpdateAtEnd: boolean,
    newUuiData?: string,
  ) {
    direction.value = CtiInteractionTypeEnum.INBOUND
    showDialog(CtiEventSource.Automatic)
    callerPhone.value = newCallerPhone
    inboundCallConfiguration.value = newInboundCallConfiguration
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    updateAtEnd.value = newUpdateAtEnd
    userToUserInformation.value = newUuiData
  }

  function newOutboundCallFromEverDesk(
    newOutboundCallConfiguration: UUID,
    newCalledPhone: string,
    newCustomerId: number | null,
    newIdTicket: number | null,
  ) {
    direction.value = CtiInteractionTypeEnum.OUTBOUND
    showDialog(CtiEventSource.Automatic)
    outboundCallConfiguration.value = [...outboundCallsMap.value.values()].find((aConf) => aConf.id === newOutboundCallConfiguration) ?? null
    calledPhone.value = newCalledPhone
    customerId.value = newCustomerId
    ticketId.value = newIdTicket
    updateAtEnd.value = true
  }

  function endCall(newIdGroup: string | null, newCalledPhone: string | null, newIdCall: string | null, updateStatus: boolean = true) {
    idGroup.value = newIdGroup
    calledPhone.value = newCalledPhone
    idCall.value = newIdCall
    if (updateStatus) {
      status.value = CtiStatus.AVAILABLE
    }
  }

  function release() {
    toReinitialized.value = true
    stopCallTimer()

    provider.value.release()
    provider.value = dummyCtiService
  }

  function setAgentStatus(state: string, breakCode?: string) {
    provider.value.setAgentStatus(state, breakCode)
  }

  async function initCtiConfig(initProviderFunction: () => Promise<void>) {
    if (!sessionStore.getAuthoritySet.has(AuthoritiesEnum.CTI_ACCESS)) {
      connectable.value = false
      voiceIsInitialized.value = true
      return
    }

    try {
      await initProviderFunction()
    } catch (e) {
      console.error(e)
      connectable.value = false
    }
  }

  async function getAgentLogin() {
    try {
      const agentData: AgentModel | null = await VoiceService.getAgentLogin()
      agentLogin.value = agentData?.providerLogin ?? null
    } catch (message) {
      console.error(message)
    }
  }

  async function getDefaultOutboundCallConfiguration() {
    return (
      [...outboundCallsMap.value.values()].find((configuration) => configuration.defaultSkill === generalConfiguration.value?.fallbackSkill) ?? null
    )
  }

  return {
    activeProvider,
    token,
    toReinitialized,
    connectable,
    voiceIsInitialized,
    dialogVisibility,
    dialogStateSource,
    callerPhone,
    provider,
    idGroup,
    inboundCallConfiguration,
    outboundCallConfiguration,
    calledPhone,
    customerId,
    url,
    inboundCallsMap,
    outboundCallsMap,
    urlString,
    idCall,
    direction,
    updateAtEnd,
    landingPage,
    status,
    agentLogin,
    ticketId,
    statusColor,
    toggleDialog,
    showDialog,
    hideDialog,
    newInboundCall,
    newInboundCallV2,
    newOutboundCallFromEverDesk,
    endCall,
    release,
    initCtiConfig,
    iframeStyle,
    incorrectUser,
    generalConfiguration,
    iframeUrlUpdate,
    userToUserInformation,
    setAgentStatus,
    getAgentLogin,
    ctiCallInfo,
    dummyCtiService,
    getDefaultOutboundCallConfiguration,
    outboundCallSource,
    callTimerHuman,
    ctiPanelVisibility,
    startCallInCtiPanel,
    wrapUpCallInCtiPanel,
  }
})
